import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { 
  CorporatePortfolioMovementService, 
  CorporateEnrollmentWithdrawalInfo, 
  ReverseCorporateWithdrawalRequest,
  CorporatePortfolioMovementResult 
} from '../../../services/corporate-portfolio-movement.service';
import { CompanyService } from '../../../services/company.service';
import { ProjectService } from '../../../services/project.service';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-corporate-reversal-wizard',
  templateUrl: './corporate-reversal-wizard.component.html',
  styleUrls: ['./corporate-reversal-wizard.component.scss']
})
export class CorporateReversalWizardComponent implements OnInit {
  // Exponer Math para el template
  Math = Math;
  
  currentStep = 1;
  
  companies: any[] = [];
  selectedCompanyId: number | null = null;
  corporateEnrollments: CorporateEnrollmentWithdrawalInfo[] = [];
  selectedEnrollment: CorporateEnrollmentWithdrawalInfo | null = null;
  projects: any[] = [];
  
  searchForm: FormGroup;
  destinationForm: FormGroup;
  valueForm: FormGroup;
  confirmForm: FormGroup;
  
  // Plan de pagos personalizado
  newPaymentPlan: { installmentNumber: number; dueDate: Date; amount: number; }[] = [];
  
  // Modal de refinanciación
  showRefinanceModal = false;
  refinanceInstallments = 2;
  minInstallments = 1;
  maxInstallments = 24;
  
  isLoading = false;
  isSearching = false;
  isSubmitting = false;
  searchPerformed = false;
  
  operationResult: CorporatePortfolioMovementResult | null = null;
  operationSuccess = false;
  operationError = '';

  constructor(
    private fb: FormBuilder,
    private corporatePortfolioService: CorporatePortfolioMovementService,
    private companyService: CompanyService,
    private projectService: ProjectService,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.searchForm = this.fb.group({
      searchTerm: ['', Validators.required]
    });
    
    this.destinationForm = this.fb.group({
      continueInSameProject: [true],
      newProjectId: [null]
    });
    
    this.valueForm = this.fb.group({
      newTotalValueCOP: [0, [Validators.min(0)]],
      newTotalValueUSD: [0, [Validators.min(0)]],
      currencyId: [1]
    });
    
    this.confirmForm = this.fb.group({
      reason: ['', [Validators.required, Validators.minLength(10)]],
      notes: ['']
    });
  }

  // Total de pasos dinámico
  get totalSteps(): number {
    const continueInSame = this.destinationForm.get('continueInSameProject')?.value;
    return continueInSame ? 5 : 7; // 7 pasos cuando hay nuevo proyecto
  }

  // Paso de confirmación según el flujo
  get confirmationStep(): number {
    const continueInSame = this.destinationForm.get('continueInSameProject')?.value;
    return continueInSame ? 4 : 6;
  }

  // Paso de resultado según el flujo
  get resultStep(): number {
    const continueInSame = this.destinationForm.get('continueInSameProject')?.value;
    return continueInSame ? 5 : 7;
  }

  ngOnInit(): void {
    this.loadProjects();
    this.route.queryParams.subscribe(params => {
      if (params['corporateEnrollmentId']) {
        this.loadEnrollmentById(+params['corporateEnrollmentId']);
      }
    });
  }

  // Helper para extraer datos de estructura paginada o array directo
  private extractData(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (response?.data) {
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    }
    return [];
  }

  loadProjects(): void {
    this.projectService.getAll().subscribe({
      next: (response) => {
        this.projects = this.extractData(response);
      },
      error: () => {}
    });
  }

  searchCompany(): void {
    if (this.searchForm.invalid) return;
    
    this.isSearching = true;
    this.searchPerformed = true;
    const term = this.searchForm.get('searchTerm')?.value;
    
    this.companyService.getAll().subscribe({
      next: (response) => {
        const allCompanies = this.extractData(response);
        this.companies = allCompanies.filter((c: any) => 
          c.name?.toLowerCase().includes(term.toLowerCase()) ||
          c.documentId?.includes(term)
        );
        this.isSearching = false;
      },
      error: () => {
        this.toastService.showToast('error', 'Error al buscar empresas');
        this.isSearching = false;
      }
    });
  }

  selectCompany(company: any): void {
    this.selectedCompanyId = company.id;
    this.loadWithdrawnCorporateEnrollments(company.id);
  }

  loadWithdrawnCorporateEnrollments(companyId: number): void {
    this.isLoading = true;
    this.corporatePortfolioService.getWithdrawnCorporateEnrollmentsByCompany(companyId).subscribe({
      next: (response) => {
        this.corporateEnrollments = this.extractData(response);
        this.isLoading = false;
        if (this.corporateEnrollments.length === 0) {
          this.toastService.showToast('warning', 'Esta empresa no tiene acuerdos corporativos retirados');
        }
      },
      error: () => {
        this.toastService.showToast('error', 'Error al cargar acuerdos');
        this.isLoading = false;
      }
    });
  }

  loadEnrollmentById(corporateEnrollmentId: number): void {
    this.isLoading = true;
    this.corporatePortfolioService.getCorporateEnrollmentWithdrawalInfo(corporateEnrollmentId).subscribe({
      next: (response) => {
        const enrollment = response.data || response;
        if (enrollment && enrollment.canBeReversed) {
          this.selectedEnrollment = enrollment;
          this.currentStep = 2;
        } else {
          this.toastService.showToast('error', 'Este acuerdo no puede ser revertido');
          this.router.navigate(['/enrollments']);
        }
        this.isLoading = false;
      },
      error: () => {
        this.toastService.showToast('error', 'Error al cargar acuerdo');
        this.isLoading = false;
      }
    });
  }

  selectEnrollment(enrollment: CorporateEnrollmentWithdrawalInfo): void {
    if (!enrollment.canBeReversed) {
      this.toastService.showToast('warning', 'Este acuerdo no está retirado');
      return;
    }
    this.selectedEnrollment = enrollment;
    this.currentStep = 2;
  }

  nextStep(): void {
    const continueInSame = this.destinationForm.get('continueInSameProject')?.value;
    
    if (this.currentStep === 1 && this.selectedEnrollment) {
      this.currentStep = 2;
    } else if (this.currentStep === 2) {
      this.currentStep = 3;
    } else if (this.currentStep === 3 && this.isDestinationValid()) {
      if (continueInSame) {
        this.currentStep = 4;
      } else {
        this.initializeValueFromPunished();
        this.currentStep = 4;
      }
    } else if (this.currentStep === 4 && !continueInSame) {
      this.generatePaymentPlan(2);
      this.currentStep = 5;
    } else if (this.currentStep === 5 && !continueInSame && this.isPlanValid) {
      this.currentStep = 6;
    }
  }

  prevStep(): void {
    if (this.currentStep > 1) {
      const continueInSame = this.destinationForm.get('continueInSameProject')?.value;
      
      if (!continueInSame && this.currentStep === 6) {
        this.currentStep = 5;
      } else if (!continueInSame && this.currentStep === 5) {
        this.currentStep = 4;
      } else {
        this.currentStep--;
      }
    }
  }

  isDestinationValid(): boolean {
    const continueInSame = this.destinationForm.get('continueInSameProject')?.value;
    if (continueInSame) return true;
    return !!this.destinationForm.get('newProjectId')?.value;
  }

  // Inicializar valores desde las cuotas castigadas
  initializeValueFromPunished(): void {
    const total = this.selectedEnrollment?.totalPendingAmount || 0;
    this.valueForm.patchValue({
      newTotalValueCOP: total,
      newTotalValueUSD: 0,
      currencyId: 1
    });
  }

  // Autollenar desde el proyecto seleccionado
  autofillFromProject(): void {
    const project = this.getSelectedProject();
    if (project) {
      this.valueForm.patchValue({
        newTotalValueCOP: project.inscriptionAmountCOP || project.inscriptionAmount || 0,
        newTotalValueUSD: project.inscriptionAmountUSD || 0
      });
    }
  }

  getSelectedProject(): any {
    const projectId = this.destinationForm.get('newProjectId')?.value;
    return this.projects.find(p => p.id === projectId);
  }

  // Modal de refinanciación
  openRefinanceModal(): void {
    this.refinanceInstallments = this.newPaymentPlan.length || 2;
    this.showRefinanceModal = true;
  }

  closeRefinanceModal(): void {
    this.showRefinanceModal = false;
  }

  confirmRefinance(): void {
    this.generatePaymentPlan(this.refinanceInstallments);
    this.closeRefinanceModal();
  }

  // Generar plan de pagos automático
  generatePaymentPlan(installmentsCount: number): void {
    const currencyId = this.valueForm.get('currencyId')?.value || 1;
    const totalValue = currencyId === 1 
      ? this.parseAmount(this.valueForm.get('newTotalValueCOP')?.value)
      : this.parseAmount(this.valueForm.get('newTotalValueUSD')?.value);
    
    const installmentAmount = totalValue / installmentsCount;
    this.newPaymentPlan = [];
    
    for (let i = 1; i <= installmentsCount; i++) {
      this.newPaymentPlan.push({
        installmentNumber: i,
        dueDate: this.calculateNextDueDate(i),
        amount: i === installmentsCount 
          ? totalValue - (installmentAmount * (installmentsCount - 1))
          : Math.floor(installmentAmount)
      });
    }
  }

  calculateNextDueDate(installmentNumber: number): Date {
    const today = new Date();
    const dueDate = new Date(today.getFullYear(), today.getMonth() + installmentNumber, 15);
    return dueDate;
  }

  addInstallment(): void {
    const nextNumber = this.newPaymentPlan.length + 1;
    this.newPaymentPlan.push({
      installmentNumber: nextNumber,
      dueDate: this.calculateNextDueDate(nextNumber),
      amount: 0
    });
  }

  removeInstallment(index: number): void {
    if (this.newPaymentPlan.length > 1) {
      this.newPaymentPlan.splice(index, 1);
      this.newPaymentPlan.forEach((plan, i) => {
        plan.installmentNumber = i + 1;
      });
    }
  }

  get isPlanValid(): boolean {
    if (this.newPaymentPlan.length === 0) return false;
    const totalPlan = this.currentPlanTotal;
    const expectedTotal = this.getActiveTotal();
    const tolerance = 1;
    return Math.abs(totalPlan - expectedTotal) <= tolerance;
  }

  get currentPlanTotal(): number {
    return this.newPaymentPlan.reduce((sum, plan) => sum + this.parseAmount(plan.amount), 0);
  }

  getActiveTotal(): number {
    const currencyId = this.valueForm.get('currencyId')?.value || 1;
    return currencyId === 1 
      ? this.parseAmount(this.valueForm.get('newTotalValueCOP')?.value)
      : this.parseAmount(this.valueForm.get('newTotalValueUSD')?.value);
  }

  getEstimatedInstallmentAmount(): number {
    const count = this.newPaymentPlan.length || 2;
    return this.getActiveTotal() / count;
  }

  parseAmount(value: any): number {
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const cleaned = value.replace(/[^\d.-]/g, '');
      const parsed = parseFloat(cleaned);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }

  submitReversal(): void {
    if (this.confirmForm.invalid || !this.selectedEnrollment || this.isSubmitting) return;
    
    const continueInSameProject = this.destinationForm.get('continueInSameProject')?.value;
    if (!continueInSameProject && !this.destinationForm.get('newProjectId')?.value) {
      this.toastService.showToast('error', 'Debe seleccionar un proyecto destino');
      return;
    }
    
    this.isSubmitting = true;
    const request: ReverseCorporateWithdrawalRequest = {
      corporateEnrollmentId: this.selectedEnrollment.corporateEnrollmentId,
      continueInSameProject: continueInSameProject,
      newProjectId: continueInSameProject ? undefined : this.destinationForm.get('newProjectId')?.value,
      reason: this.confirmForm.get('reason')?.value,
      notes: this.confirmForm.get('notes')?.value
    };

    // Agregar valores personalizados si es un nuevo proyecto
    if (!continueInSameProject && this.newPaymentPlan.length > 0) {
      request.newTotalValueCOP = this.parseAmount(this.valueForm.get('newTotalValueCOP')?.value);
      request.newTotalValueUSD = this.parseAmount(this.valueForm.get('newTotalValueUSD')?.value);
      request.currencyId = this.valueForm.get('currencyId')?.value;
      request.newPaymentPlan = this.newPaymentPlan.map(p => ({
        installmentNumber: p.installmentNumber,
        dueDate: p.dueDate,
        amount: this.parseAmount(p.amount)
      }));
    }

    this.corporatePortfolioService.reverseCorporateWithdrawal(request).subscribe({
      next: (response) => {
        const result = response.data || response;
        this.operationResult = result;
        this.isSubmitting = false;
        this.operationSuccess = result.success;
        this.operationError = result.success ? '' : (result.message || 'Error al procesar reversión');
        this.currentStep = this.resultStep;
      },
      error: () => {
        this.operationSuccess = false;
        this.operationError = 'Error al procesar reversión. Por favor, intente nuevamente.';
        this.currentStep = this.resultStep;
        this.isSubmitting = false;
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/enrollments']);
  }

  goToEnrollmentsList(): void {
    this.router.navigate(['/enrollments']);
  }

  startNewReversal(): void {
    this.currentStep = 1;
    this.selectedCompanyId = null;
    this.selectedEnrollment = null;
    this.corporateEnrollments = [];
    this.companies = [];
    this.searchPerformed = false;
    this.operationResult = null;
    this.operationSuccess = false;
    this.operationError = '';
    this.newPaymentPlan = [];
    this.searchForm.reset();
    this.destinationForm.reset({ continueInSameProject: true });
    this.valueForm.reset({ newTotalValueCOP: 0, newTotalValueUSD: 0, currencyId: 1 });
    this.confirmForm.reset();
  }

  formatCurrency(amount: number, currency: string = 'COP'): string {
    if (currency === 'USD') {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
    }
    return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(amount);
  }
}

